import React, { useState, useEffect, useRef, useCallback, useMemo, useReducer } from 'react';
import { messagesAPI, userAPI } from '../services/api';
import { getProfilePicture, getFirstName, isOnline, formatDate } from '../utils/helpers';
import { FaSmile, FaGift, FaPaperPlane, FaSearch, FaArrowLeft, FaMicrophone, FaPaperclip, FaCheck, FaCheckDouble, FaBell, FaCog, FaUser, FaSignOutAlt, FaCamera, FaUserPlus, FaTimes } from 'react-icons/fa';
import EmojiPicker from '../components/EmojiPicker';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFriend } from '../context/FriendContext';
import { usePermissions } from '../hooks/usePermissions';
import ReportModal from '../components/ReportModal';
import BlockModal from '../components/BlockModal';
import Header from '../components/Header';

// Messages reducer for more predictable state management
const messagesReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MESSAGES':
      return action.payload;
    case 'ADD_MESSAGES':
      const existingIds = new Set(state.map(m => m.id));
      const newMessages = action.payload.filter(m => !existingIds.has(m.id));
      if (newMessages.length > 0) {
        return [...state, ...newMessages];
      }
      return state;
    case 'CLEAR_MESSAGES':
      return [];
    case 'ADD_TEMP_MESSAGE':
      return [...state, action.payload];
    case 'REMOVE_TEMP_MESSAGE':
      return state.filter(msg => msg.id !== action.payload);
    default:
      return state;
  }
};

// Gift emojis for chat messages
const GIFT_EMOJIS = {
  'Rose': '🌹',
  'Heart': '❤️',
  'Chocolate': '🍫',
  'Cake': '🎂',
  'Teddy Bear': '🧸',
  'Ring': '💍',
  'Crown': '👑',
  'Car': '🚗',
  'House': '🏠',
  'Diamond': '💎',
  'Coffee': '☕',
  'Wine': '🍷',
  'Beer': '🍺',
  'Pizza': '🍕',
  'Burger': '🍔',
  'Ice Cream': '🍦',
  'Cupcake': '🧁',
  'Cookie': '🍪',
  'Donut': '🍩',
  'Candy': '🍬',
  'Lollipop': '🍭',
  'Lipstick': '💄',
  'Shoes': '👠',
  'Plane': '✈️'
};

const getGiftEmoji = (giftId, giftName) => {
  // First try exact match with database name
  if (giftName && typeof giftName === 'string' && giftName.trim()) {
    if (GIFT_EMOJIS[giftName.trim()]) {
      return GIFT_EMOJIS[giftName.trim()];
    }
    // Then try case-insensitive match
    const nameKey = giftName.toLowerCase().trim();
    if (GIFT_EMOJIS[nameKey]) {
      return GIFT_EMOJIS[nameKey];
    }
  }

  // Then try to match by ID
  if (giftId && GIFT_EMOJIS[giftId]) {
    return GIFT_EMOJIS[giftId];
  }

  // Default fallback
  return '🎁';
};

const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, socket, logout: authLogout } = useAuth();
  const { acceptFriendRequest, declineFriendRequest } = useFriend();
  const { permissions, requestBrowserPermission } = usePermissions();


  const logout = async () => {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Messages: Logout error:', error);
    }
  };

  // Refs - stable across renders
  const pollingIntervalRef = useRef(null);
  const isFetchingRef = useRef(false);
  const startPollingRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const previousChatIdRef = useRef(null);

  // Cleanup refs on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isFetchingRef.current = false;
      startPollingRef.current = null;
      lastMessageIdRef.current = null;
    };
  }, []);

  // Core state
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, dispatchMessages] = useReducer(messagesReducer, []);
  const [loading, setLoading] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [currentChatId, setCurrentChatId] = useState(null); // Track chat ID separately to break render loops
  const [chatsLoading, setChatsLoading] = useState(true);
  const [chatsError, setChatsError] = useState(null);

  // Update currentChatId when activeChat changes - BREAK THE LOOP!
  useEffect(() => {
    const newChatId = activeChat?.id || null;
    if (newChatId !== currentChatId) {
      setCurrentChatId(newChatId);
    }
  }, [activeChat?.id]);
  
  // UI state
  const [messageText, setMessageText] = useState('');

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showChatList, setShowChatList] = useState(true);
  const [typingUsers, setTypingUsers] = useState({}); // Track typing status per user
  const [isRecording, setIsRecording] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [respondedRequests, setRespondedRequests] = useState(new Set()); // Track responded photo requests
  const [respondedFriendRequests, setRespondedFriendRequests] = useState(new Set()); // Track responded friend requests

  
  // Refs
  const desktopTextareaRef = useRef(null);
  const mobileTextareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Load chats on mount
  useEffect(() => {
    fetchChats();
    // Scroll to top (header) when page loads
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, []);

  // Handle URL chat parameter
  useEffect(() => {
    const chatId = searchParams.get('chat');
    const isDirect = searchParams.get('direct') === 'true';

    if (chatId && !activeChat) {
      console.log('🔗 Messages: Direct chat access for user ID:', chatId);
      const tempChat = {
        id: parseInt(chatId),
        full_name: isDirect ? 'Opening chat...' : 'Loading...',
        profile_picture: null,
        isNewChat: true
      };
      setActiveChat(tempChat);

      // If direct access, hide chat list immediately for better UX
      if (isDirect) {
        setShowChatList(false);
      }

      navigate('/messages', { replace: true });
    }
  }, [searchParams, activeChat, navigate]);

  // Fetch chats list
  const fetchChats = async () => {
    try {
      setChatsLoading(true);
      setChatsError(null);

      const response = await messagesAPI.getChats();
      if (response?.success && Array.isArray(response.chats)) {
        const validChats = response.chats.filter(chat => chat && chat.id);
        setChats(validChats);

        // Auto-select first chat if none selected - BUT DON'T OVERRIDE DIRECT CHAT ACCESS
        // Only auto-select if no chat is active AND we're not in direct access mode
        if (validChats.length > 0 && !activeChat && !searchParams.get('chat')) {
          setActiveChat(validChats[0]);
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to fetch chats:', error);
      setChatsError(error.message || 'Failed to load chats');
      toast.error('Failed to load chats');
    } finally {
      setChatsLoading(false);
    }
  };


  // Fetch messages for a chat - NO dependencies to avoid loops
  const fetchMessages = useCallback(async (userId, isNewChat = false) => {
    if (isFetchingRef.current || !userId) {
      console.log('🚫 Skipping fetch - already fetching or no userId');
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const response = await messagesAPI.getChat(userId);

      if (!response || !response.success || !Array.isArray(response.messages)) {
        throw new Error('Invalid response format');
      }

      const loadedMessages = response.messages;
      
      // CRITICAL: Set messages - MUST persist
      dispatchMessages({ type: 'SET_MESSAGES', payload: loadedMessages });

      // Update lastMessageId
      if (loadedMessages.length > 0) {
        const lastId = response.lastMessageId || loadedMessages[loadedMessages.length - 1].id;
        setLastMessageId(lastId);
        lastMessageIdRef.current = lastId;
      } else {
        setLastMessageId(null);
        lastMessageIdRef.current = null;
      }

      // Update user info for new chats - BUT DON'T TRIGGER RERENDERS!
      if (isNewChat && response.user) {
        // Use setTimeout to avoid immediate re-render loops
        setTimeout(() => {
          setActiveChat(prev => ({
            ...prev,
            ...response.user,
            isNewChat: false
          }));
        }, 0);
      }

      // Start polling AFTER messages are set (use ref to avoid dependency loop)
      // Only start polling if we have messages and polling ref is available
      if (loadedMessages.length > 0 && startPollingRef.current) {
        // Start polling immediately, not after timeout
        startPollingRef.current(userId);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
      dispatchMessages({ type: 'CLEAR_MESSAGES' });
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []); // NO dependencies - use refs instead

  // Check for new messages (polling) - MORE STABLE
  const checkNewMessages = useCallback(async (userId) => {
    const lastId = lastMessageIdRef.current;

    // Only poll for the currently active chat
    if (!userId || userId !== currentChatId) {
      return;
    }

    try {
      const response = await messagesAPI.getNewMessages(userId, lastId || 0);

      if (response?.success && response?.messages?.length > 0) {
        // Use dispatch for more predictable updates
        // Double-check we're still on the same chat
        if (userId !== currentChatId) {
          return;
        }

        dispatchMessages({ type: 'ADD_MESSAGES', payload: response.messages });

        // Update lastMessageId atomically
        const newLastId = response.lastMessageId || response.messages[response.messages.length - 1].id;
        setLastMessageId(newLastId);
        lastMessageIdRef.current = newLastId;
      }
    } catch (error) {
      console.error('Failed to check new messages:', error);
    }
  }, []); // Removed dependency to prevent recreation

  // Start polling for new messages - MORE STABLE
  const startPolling = useCallback((userId) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Polling interval for new messages using getNewMessages endpoint (every 3 seconds for real-time feel)
  // This is already optimized to use the backend's efficient getNewMessages endpoint
  // which only returns messages newer than the specified lastMessageId
    pollingIntervalRef.current = setInterval(() => {
      // Double-check user is still active before polling
      if (currentChatId === userId) {
        checkNewMessages(userId);
      } else {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    }, 3000); // 3 seconds for real-time feel
  }, [checkNewMessages]);
  
  // Update ref when startPolling changes
  useEffect(() => {
    startPollingRef.current = startPolling;
  }, [startPolling]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);


  // Load messages when currentChatId changes - BREAK THE RENDER LOOP!
  useEffect(() => {
    if (!currentChatId) {
      return;
    }

    // Prevent duplicate fetches (Strict Mode protection)
    const fetchKey = `fetch_${currentChatId}_${Date.now()}`;

    // Stop any existing polling first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Reset state for new chat
    setLastMessageId(null);
    lastMessageIdRef.current = null;
    isFetchingRef.current = false;
    setRespondedRequests(new Set()); // Reset responded requests for new chat

    // Only clear messages when chat actually changes (not on Strict Mode re-renders)
    if (previousChatIdRef.current !== currentChatId) {
      dispatchMessages({ type: 'CLEAR_MESSAGES' });
      previousChatIdRef.current = currentChatId;
    }

    // Fetch messages immediately with timeout to prevent rapid calls
    const timeoutId = setTimeout(() => {
      fetchMessages(currentChatId, activeChat?.isNewChat);
    }, 50); // Small delay to prevent Strict Mode duplicates

    return () => {
      clearTimeout(timeoutId);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [currentChatId]); // ONLY depend on currentChatId - breaks the loop!

  // Scroll to bottom when messages change - ONLY when new messages are added
  useEffect(() => {
    const shouldScroll = messages.length > 0 && loading === false;
    if (shouldScroll) {
      // Use timeout to ensure DOM is updated
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [messages.length, loading]); // Only depend on length and loading state

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !activeChat) return;

    const messageToSend = messageText.trim();
    setMessageText('');

    // Optimistic update - safe localStorage access
    let currentUser = null;
    try {
      const userData = localStorage.getItem('user');
      currentUser = userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
    }

    const tempMessage = {
      id: Date.now(),
      message: messageToSend,
      time: Date.now() / 1000,
      is_read: 0,
      sender: {
        id: currentUser?.id || null,
        full_name: currentUser?.full_name || 'You',
        profile_picture: currentUser?.profile_picture || null
      }
    };

    dispatchMessages({ type: 'ADD_TEMP_MESSAGE', payload: tempMessage });
    setTypingUsers(prev => ({ ...prev, [activeChat.id]: true }));

    // Auto-stop typing after 3 seconds
    setTimeout(() => {
      setTypingUsers(prev => ({ ...prev, [activeChat.id]: false }));
    }, 3000);

    try {
      await messagesAPI.sendMessage({
        receiver_id: activeChat.id,
        message: messageToSend,
      });

      // Refresh to get real message - immediate call instead of timeout
      console.log('🔄 Refreshing messages after send...');
      checkNewMessages(activeChat.id);

      setTypingUsers(prev => ({ ...prev, [activeChat.id]: false }));
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      dispatchMessages({ type: 'REMOVE_TEMP_MESSAGE', payload: tempMessage.id });
      setMessageText(messageToSend);
      setTypingUsers(prev => ({ ...prev, [activeChat.id]: false }));
    }
  };

  const handleEmojiSelect = (emoji) => {
    setMessageText((prev) => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleFriendRequestResponse = async (userId, action) => {
    try {
      if (action === 'accept') {
        await acceptFriendRequest(userId);
      } else if (action === 'decline') {
        await declineFriendRequest(userId);
      }

      // Mark this request as responded
      setRespondedFriendRequests(prev => new Set([...prev, userId]));

      // Refresh messages to show updated state
      setTimeout(() => {
        checkNewMessages(activeChat.id);
      }, 1000);

    } catch (error) {
      console.error('Friend request response error:', error);
      toast.error('Failed to respond to friend request');
    }
  };

  const handlePhotoRequestResponse = async (requestId, action) => {
    try {
      await userAPI.respondToPhotoAccess(requestId, action);

      // Mark this request as responded
      setRespondedRequests(prev => new Set([...prev, requestId]));

      if (action === 'approve') {
        toast.success('Photo access granted!');

        // Photo access approved - update localStorage for immediate UI update
        const storageKey = `approvedPhotoRequests_user_${user.id}`;
        const approvedRequests = JSON.parse(localStorage.getItem(storageKey) || '{}');
        approvedRequests[activeChat.id] = {
          approved: true,
          timestamp: Date.now()
        };
        localStorage.setItem(storageKey, JSON.stringify(approvedRequests));

        // Send approval response as photo request message to requester
        await messagesAPI.sendMessage({
          receiver_id: activeChat.id,
          is_photo_request: 2, // 2 = approved response
          photo_request_id: requestId,
          message: 'Photo access approved! You can now see my private photos. 📷'
        });
      } else if (action === 'reject') {
        toast.info('Photo access request rejected');
        // Send rejection response as photo request message to requester
        await messagesAPI.sendMessage({
          receiver_id: activeChat.id,
          is_photo_request: 3, // 3 = rejected response
          photo_request_id: requestId,
          message: 'Photo access request declined. Maybe another time! 🙏'
        });
      } else if (action === 'revoke') {
        toast.info('Photo access revoked');

        // Photo access revoked - ProfileDetail will check server-side
        // No need for localStorage as we now use getApprovedPhotoAccess API

        // Trigger UI update for ProfileDetail page (if open)
        window.dispatchEvent(new CustomEvent('photoAccessRevoked', {
          detail: { profileId: parseInt(activeChat.id) }
        }));

        // Send revoke response as photo request message to requester
        await messagesAPI.sendMessage({
          receiver_id: activeChat.id,
          is_photo_request: 4, // 4 = revoked response
          photo_request_id: requestId,
          message: 'Photo access has been revoked. Sorry for any inconvenience! 🤝'
        });
      }

      // Refresh messages to show updated state
      setTimeout(() => {
        checkNewMessages(activeChat.id);
      }, 1000);

    } catch (error) {
      console.error('Photo request response error:', error);
      toast.error('Failed to respond to photo request');
    }
  };

  const handleTextareaChange = (e, ref) => {
    setMessageText(e.target.value);
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
    }
  };

  const handleChatSelect = (chat) => {
    console.log('📱 Mobile: Chat selected, switching to chat view');
    setActiveChat(chat);
    setShowChatList(false);
  };

  const handleBackToList = () => {
    console.log('📱 Mobile: Back button pressed, switching to chat list');
    setShowChatList(true);
  };

  const filteredChats = useMemo(() =>
    chats.filter((chat) =>
      chat?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [chats, searchTerm]
  );

  // Memoize profile pictures
  const activeChatProfilePic = useMemo(() =>
    activeChat ? getProfilePicture(activeChat) : null,
    [activeChat?.profile_picture, activeChat?.id]
  );

  const chatProfilePicsMap = useMemo(() => {
    const map = new Map();
    chats.forEach(chat => {
      if (chat?.id) {
        map.set(chat.id, getProfilePicture(chat));
      }
    });
    return map;
  }, [chats.map(c => `${c?.id}-${c?.profile_picture}`).join(',')]);

  // Loading state
  if (chatsLoading) {
    return (
      <div className="matchus-messages">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-2xl mb-4">💬</div>
            <h2 className="text-xl font-semibold mb-2">Loading Messages...</h2>
            <p className="text-gray-500 mt-2">Fetching your conversations...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (chatsError) {
    return (
      <div className="matchus-messages">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-2xl mb-4">❌</div>
            <h2 className="text-xl font-semibold mb-2">Failed to Load Messages</h2>
            <p className="text-gray-500 mt-2">{chatsError}</p>
            <button
              onClick={fetchChats}
              className="mt-4 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="matchus-messages-page">
      <Header title="Messages" />

      {/* Desktop Layout */}
      <div className="hidden md:flex h-screen">
        {/* Chat List */}
        <div className="matchus-chat-list-desktop">
          <div className="matchus-chat-header">
            <div className="matchus-search-container">
              <FaSearch className="matchus-search-icon" />
              <input
                type="text"
                placeholder="Search Conversations"
                className="matchus-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="matchus-chats-container">
            {filteredChats.length === 0 ? (
              <div className="matchus-chats-empty">
                <div className="matchus-empty-icon">💬</div>
                <h3>No conversations yet</h3>
                <p>Start swiping to find matches and begin chatting!</p>
              </div>
            ) : (
              filteredChats
                .filter(chat => chat && chat.id)
                .map((chat) => (
                  <div
                    key={chat.id}
                    className={`matchus-chat-item ${activeChat?.id === chat.id ? 'active' : ''}`}
                    onClick={() => handleChatSelect(chat)}
                  >
                    <div className="matchus-chat-avatar">
                      <img
                        src={chatProfilePicsMap.get(chat.id) || getProfilePicture(chat)}
                        alt={chat.full_name || 'User'}
                        className="matchus-chat-avatar-img"
                      />
                      {isOnline(chat.last_active) && (
                        <div className="matchus-chat-online"></div>
                      )}
                    </div>
                    <div className="matchus-chat-info">
                      <div className="matchus-chat-header-info">
                        <h4 className="matchus-chat-name">
                          {getFirstName(chat.full_name || 'Unknown User')}
                        </h4>
                        <span className="matchus-chat-time">
                          {chat.last_message_time ? formatDate(chat.last_message_time) : ''}
                        </span>
                      </div>
                      <p className="matchus-chat-preview">
                        {chat.last_message || 'No messages yet'}
                      </p>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>

        {/* Active Chat */}
        {activeChat ? (
          <div className="matchus-chat-view-desktop">
            <div className="matchus-chat-view-header">
              <div className="matchus-chat-partner">
                <div className="matchus-chat-partner-avatar">
                  <img
                    src={activeChatProfilePic}
                    alt={activeChat.full_name}
                    className="matchus-chat-partner-img"
                  />
                  {isOnline(activeChat.last_active) && (
                    <div className="matchus-chat-partner-online"></div>
                  )}
                </div>
                <div className="matchus-chat-partner-info">
                  <h3 className="matchus-chat-partner-name">
                    {getFirstName(activeChat.full_name || 'Unknown User')}
                  </h3>
                  <div className="matchus-chat-partner-status">
                    {typingUsers[activeChat?.id] ? (
                      <span className="matchus-typing-indicator">typing...</span>
                    ) : (
                      <span className={isOnline(activeChat.last_active) ? 'online' : 'offline'}>
                        {isOnline(activeChat.last_active) ? 'Active now' : 'Offline'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="matchus-chat-header-actions">
                <button
                  className="matchus-chat-action-btn"
                  title="Voice Call"
                  onClick={async () => {
                    if (!permissions.microphone?.granted) {
                      const granted = await requestBrowserPermission('microphone');
                      if (!granted) return;
                    }
                    toast.info('Voice calling feature coming soon!');
                  }}
                >
                  📞
                </button>
                <button
                  className="matchus-chat-action-btn"
                  title="Video Call"
                  onClick={async () => {
                    if (!permissions.camera?.granted) {
                      const cameraGranted = await requestBrowserPermission('camera');
                      if (!cameraGranted) return;
                    }
                    if (!permissions.microphone?.granted) {
                      const micGranted = await requestBrowserPermission('microphone');
                      if (!micGranted) return;
                    }
                    toast.info('Video calling feature coming soon!');
                  }}
                >
                  📹
                </button>
                <div className="matchus-chat-action-menu">
                  <button className="matchus-chat-action-btn" title="More Options">
                    ⋮
                  </button>
                  <div className="matchus-chat-dropdown">
                    <Link
                      to={`/user/profile/${activeChat?.id}`}
                      className="matchus-chat-dropdown-item"
                    >
                      👤 View Profile
                    </Link>
                    <button
                      className="matchus-chat-dropdown-item"
                      onClick={() => setShowReportModal(true)}
                    >
                      🚨 Report Conversation
                    </button>
                    <button
                      className="matchus-chat-dropdown-item"
                      onClick={() => setShowBlockModal(true)}
                    >
                      🚫 Block User
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="matchus-messages-container">
              {loading ? (
                <div className="matchus-messages-loading">
                  <div className="matchus-spinner-small"></div>
                  <span>Loading messages...</span>
                </div>
              ) : !messages || messages.length === 0 ? (
                <div className="matchus-messages-empty">
                  <div className="matchus-empty-icon">💫</div>
                  <h4>Say hello!</h4>
                  <p>Start the conversation with {getFirstName(activeChat.full_name)}</p>
                </div>
              ) : (
                (() => {
                  console.log('🎯 Rendering messages:', messages.length, 'messages');
                  return messages.map((message) => {
                  const isReceived =
                    message.sender?.id &&
                    activeChat?.id &&
                    message.sender.id === activeChat.id;


                  return (
                    <div
                      key={message.id}
                      className={`matchus-message ${isReceived ? 'received' : 'sent'}`}
                    >
                    <div className="matchus-message-content">
                      {message.is_gift ? (
                        <div className="chat-gift-message">
                          <div className="chat-gift-emoji">
                            {getGiftEmoji(message.gift_id, message.gift_name)}
                          </div>
                          <div className="chat-gift-info">
                            <span className="chat-gift-name">Sent a gift: {message.gift_name || 'Special Gift'}</span>
                            <p className="chat-gift-text">🎁 Gift sent!</p>
                          </div>
                        </div>
                      ) : message.is_friend_request ? (
                        <div className="chat-friend-request-message">
                          <FaUserPlus className="chat-action-icon" />
                          <span className="chat-action-text">
                            {isReceived ? 'Sent you a friend request' : 'Sent a friend request'}
                          </span>
                          {isReceived && !respondedFriendRequests.has(message.sender.id) && (
                            <div className="chat-friend-request-actions">
                              <button
                                className="chat-action-btn accept"
                                onClick={() => handleFriendRequestResponse(message.sender.id, 'accept')}
                              >
                                <FaCheck className="mr-1" />
                                Accept
                              </button>
                              <button
                                className="chat-action-btn reject"
                                onClick={() => handleFriendRequestResponse(message.sender.id, 'decline')}
                              >
                                <FaTimes className="mr-1" />
                                Decline
                              </button>
                            </div>
                          )}
                        </div>
                      ) : message.is_photo_request ? (
                        <div className="chat-photo-request-message">
                          <FaCamera className="chat-action-icon" />
                          <span className="chat-action-text">
                            {message.is_photo_request === 1 ? (
                              // Photo request
                              isReceived
                                ? `${getFirstName(message.sender.full_name)} sent you a photo access request 📷`
                                : `You sent a photo access request to ${getFirstName(activeChat.full_name)} 📷`
                            ) : message.is_photo_request === 2 ? (
                              // Approved
                              isReceived
                                ? `${getFirstName(message.sender.full_name)} approved your photo access request! 📷`
                                : `You approved ${getFirstName(activeChat.full_name)}'s photo access request 📷`
                            ) : message.is_photo_request === 3 ? (
                              // Rejected
                              isReceived
                                ? `${getFirstName(message.sender.full_name)} declined your photo access request 🙏`
                                : `You declined ${getFirstName(activeChat.full_name)}'s photo access request 🙏`
                            ) : message.is_photo_request === 4 ? (
                              // Revoked
                              isReceived
                                ? `${getFirstName(message.sender.full_name)} revoked your photo access 🤝`
                                : `You revoked ${getFirstName(activeChat.full_name)}'s photo access 🤝`
                            ) : (
                              // Unknown
                              'Photo access message 📷'
                            )}
                          </span>
                          {message.is_photo_request === 1 && isReceived && !respondedRequests.has(message.photo_request_id) && (
                            <div className="chat-photo-request-actions">
                              <button
                                className="chat-action-btn accept"
                                onClick={() => handlePhotoRequestResponse(message.photo_request_id, 'approve')}
                              >
                                <FaCheck className="mr-1" />
                                Accept
                              </button>
                              <button
                                className="chat-action-btn reject"
                                onClick={() => handlePhotoRequestResponse(message.photo_request_id, 'reject')}
                              >
                                <FaTimes className="mr-1" />
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="matchus-message-text">{message.message}</p>
                      )}
                      <div className="matchus-message-footer">
                        <span className="matchus-message-time">
                          {formatDate(message.time)}
                        </span>
                        {!isReceived && (
                          <div className="matchus-message-status">
                            <FaCheckDouble className="matchus-status-icon read" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })
                })()
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="matchus-message-input-container">
              <form onSubmit={handleSendMessage} className="matchus-message-form">
                <button
                  type="button"
                  className="matchus-input-btn matchus-emoji-btn"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  title="Add emoji"
                >
                  <FaSmile />
                </button>

                <button
                  type="button"
                  className="matchus-input-btn matchus-attach-btn"
                  onClick={() => toast.info('Attachment feature coming soon!')}
                  title="Add attachment"
                >
                  <FaPaperclip />
                </button>

                <textarea
                  ref={desktopTextareaRef}
                  value={messageText}
                  onChange={(e) => handleTextareaChange(e, desktopTextareaRef)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                  placeholder="Type a message..."
                  className="matchus-message-input"
                  rows="1"
                />

                {messageText.trim() ? (
                  <button
                    type="submit"
                    className="matchus-input-btn matchus-send-btn active"
                    title="Send message"
                  >
                    <FaPaperPlane />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="matchus-input-btn matchus-voice-btn"
                    onClick={async () => {
                      if (!permissions.microphone?.granted) {
                        const granted = await requestBrowserPermission('microphone');
                        if (!granted) return;
                      }
                      setIsRecording(!isRecording);
                    }}
                    title="Record voice message"
                  >
                    <FaMicrophone />
                  </button>
                )}
              </form>

              {showEmojiPicker && (
                <div className="matchus-emoji-picker-container">
                  <EmojiPicker onSelect={handleEmojiSelect} />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="matchus-chat-view-desktop">
            <div className="matchus-messages-empty">
              <div className="matchus-empty-icon">💬</div>
              <h3>Select a conversation</h3>
              <p>Choose a chat from the list to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        {showChatList ? (
          <div className="matchus-chat-list">
            <div className="matchus-chat-header">
              <div className="matchus-search-container">
                <FaSearch className="matchus-search-icon" />
                <input
                  type="text"
                  placeholder="Search Conversations"
                  className="matchus-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="matchus-chats-container">
              {filteredChats.length === 0 ? (
                <div className="matchus-chats-empty">
                  <div className="matchus-empty-icon">💬</div>
                  <h3>No conversations yet</h3>
                  <p>Start swiping to find matches and begin chatting!</p>
                </div>
              ) : (
                filteredChats
                  .filter(chat => chat && chat.id)
                  .map((chat) => (
                    <div
                      key={chat.id}
                      className="matchus-chat-item"
                      onClick={() => handleChatSelect(chat)}
                    >
                      <div className="matchus-chat-avatar">
                        <img
                          src={chatProfilePicsMap.get(chat.id) || getProfilePicture(chat)}
                          alt={chat.full_name || 'User'}
                          className="matchus-chat-avatar-img"
                        />
                        {isOnline(chat.last_active) && (
                          <div className="matchus-chat-online"></div>
                        )}
                      </div>
                      <div className="matchus-chat-info">
                        <div className="matchus-chat-header-info">
                          <h4 className="matchus-chat-name">
                            {getFirstName(chat.full_name || 'Unknown User')}
                          </h4>
                          <span className="matchus-chat-time">
                            {chat.last_message_time ? formatDate(chat.last_message_time) : ''}
                          </span>
                        </div>
                        <p className="matchus-chat-preview">
                          {chat.last_message || 'No messages yet'}
                        </p>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        ) : (
          activeChat && (
            <div className="matchus-chat-view-mobile">
              <div className="matchus-chat-view-header">
                <button
                  className="matchus-back-btn"
                  onClick={handleBackToList}
                >
                  <FaArrowLeft />
                </button>
                <div className="matchus-chat-partner">
                  <div className="matchus-chat-partner-avatar">
                    <img
                      src={activeChatProfilePic}
                      alt={activeChat.full_name}
                      className="matchus-chat-partner-img"
                    />
                    {isOnline(activeChat.last_active) && (
                      <div className="matchus-chat-partner-online"></div>
                    )}
                  </div>
                  <div className="matchus-chat-partner-info">
                    <h3 className="matchus-chat-partner-name">
                      {getFirstName(activeChat.full_name || 'Unknown User')}
                    </h3>
                    <div className="matchus-chat-partner-status">
                      {typingUsers[activeChat?.id] ? (
                        <span className="matchus-typing-indicator">typing...</span>
                      ) : (
                        <span className={isOnline(activeChat.last_active) ? 'online' : 'offline'}>
                          {isOnline(activeChat.last_active) ? 'Active now' : 'Offline'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="matchus-chat-header-actions">
                  <button
                    className="matchus-chat-action-btn"
                    title="Voice Call"
                    onClick={async () => {
                      if (!permissions.microphone?.granted) {
                        const granted = await requestBrowserPermission('microphone');
                        if (!granted) return;
                      }
                      toast.info('Voice calling feature coming soon!');
                    }}
                  >
                    📞
                  </button>
                  <button
                    className="matchus-chat-action-btn"
                    title="Video Call"
                    onClick={async () => {
                      if (!permissions.camera?.granted) {
                        const cameraGranted = await requestBrowserPermission('camera');
                        if (!cameraGranted) return;
                      }
                      if (!permissions.microphone?.granted) {
                        const micGranted = await requestBrowserPermission('microphone');
                        if (!micGranted) return;
                      }
                      toast.info('Video calling feature coming soon!');
                    }}
                  >
                    📹
                  </button>
                  <div className="matchus-chat-action-menu">
                    <button className="matchus-chat-action-btn" title="More Options">
                      ⋮
                    </button>
                    <div className="matchus-chat-dropdown">
                      <Link
                        to={`/user/profile/${activeChat?.id}`}
                        className="matchus-chat-dropdown-item"
                      >
                        👤 View Profile
                      </Link>
                      <button
                        className="matchus-chat-dropdown-item"
                        onClick={() => setShowReportModal(true)}
                      >
                        🚨 Report Conversation
                      </button>
                      <button
                        className="matchus-chat-dropdown-item"
                        onClick={() => setShowBlockModal(true)}
                      >
                        🚫 Block User
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="matchus-messages-container">
                {loading ? (
                  <div className="matchus-messages-loading">
                    <div className="matchus-spinner-small"></div>
                    <span>Loading messages...</span>
                  </div>
                ) : !messages || messages.length === 0 ? (
                  <div className="matchus-messages-empty">
                    <div className="matchus-empty-icon">💫</div>
                    <h4>Say hello!</h4>
                    <p>Start the conversation with {getFirstName(activeChat.full_name)}</p>
                    <p className="text-xs text-gray-400 mt-2">Messages: {messages?.length || 0}</p>
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-gray-400 p-2">Total messages: {messages.length}</div>
                    {messages.map((message) => {
                      const isReceived =
                        message.sender?.id &&
                        activeChat?.id &&
                        message.sender.id === activeChat.id;

                      return (
                        <div
                          key={message.id}
                          className={`matchus-message ${isReceived ? 'received' : 'sent'}`}
                        >
                          <div className="matchus-message-content">
                            {message.is_gift ? (
                              <div className="chat-gift-message">
                                <div className="chat-gift-emoji">
                                  {getGiftEmoji(message.gift_id, message.gift_name)}
                                </div>
                                <div className="chat-gift-info">
                                  <span className="chat-gift-name">Sent a gift: {message.gift_name || 'Special Gift'}</span>
                                  <p className="chat-gift-text">🎁 Gift sent!</p>
                                </div>
                              </div>
                            ) : message.is_friend_request ? (
                              <div className="chat-friend-request-message">
                                <FaUserPlus className="chat-action-icon" />
                                <span className="chat-action-text">
                                  {isReceived ? 'Sent you a friend request' : 'Sent a friend request'}
                                </span>
                                {isReceived && !respondedFriendRequests.has(message.sender.id) && (
                                  <div className="chat-friend-request-actions">
                                    <button
                                      className="chat-action-btn accept"
                                      onClick={() => handleFriendRequestResponse(message.sender.id, 'accept')}
                                    >
                                      <FaCheck className="mr-1" />
                                      Accept
                                    </button>
                                    <button
                                      className="chat-action-btn reject"
                                      onClick={() => handleFriendRequestResponse(message.sender.id, 'decline')}
                                    >
                                      <FaTimes className="mr-1" />
                                      Decline
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : message.is_photo_request ? (
                              <div className="chat-photo-request-message">
                                <FaCamera className="chat-action-icon" />
                                <span className="chat-action-text">Requested to view private photos</span>
                              </div>
                            ) : (
                              <p className="matchus-message-text">{message.message}</p>
                            )}
                            <div className="matchus-message-footer">
                              <span className="matchus-message-time">
                                {formatDate(message.time)}
                              </span>
                              {!isReceived && (
                                <div className="matchus-message-status">
                                  <FaCheckDouble className="matchus-status-icon read" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="matchus-message-input-container" style={{ border: '2px solid red' }}>
                <form onSubmit={handleSendMessage} className="matchus-message-form">
                  <button
                    type="button"
                    className="matchus-input-btn matchus-emoji-btn"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    title="Add emoji"
                  >
                    <FaSmile />
                  </button>

                  <button
                    type="button"
                    className="matchus-input-btn matchus-attach-btn"
                    onClick={() => toast.info('Attachment feature coming soon!')}
                    title="Add attachment"
                  >
                    <FaPaperclip />
                  </button>

                  <textarea
                    ref={mobileTextareaRef}
                    value={messageText}
                    onChange={(e) => handleTextareaChange(e, mobileTextareaRef)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                    placeholder="Type a message..."
                    className="matchus-message-input"
                    rows="1"
                  />

                  {messageText.trim() ? (
                    <button
                      type="submit"
                      className="matchus-input-btn matchus-send-btn active"
                      title="Send message"
                    >
                      <FaPaperPlane />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="matchus-input-btn matchus-voice-btn"
                      onClick={async () => {
                        if (!permissions.microphone?.granted) {
                          const granted = await requestBrowserPermission('microphone');
                          if (!granted) return;
                        }
                        setIsRecording(!isRecording);
                      }}
                      title="Record voice message"
                    >
                      <FaMicrophone />
                    </button>
                  )}
                </form>

                {showEmojiPicker && (
                  <div className="matchus-emoji-picker-container">
                    <EmojiPicker onSelect={handleEmojiSelect} />
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>

      {/* Modals */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        userId={activeChat?.id}
        userName={activeChat?.full_name}
      />

      <BlockModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        userId={activeChat?.id}
        userName={activeChat?.full_name}
      />
    </div>
  );
};

export default Messages;
